import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { funnelsApi } from '../../lib/api.js';
import { formatDimensionName, formatDimensionValue } from '../../lib/dimension-labels.js';
import { formatNumber } from '../../lib/formatters.js';
import { queryKeys } from '../../lib/query-keys.js';
import { buildPivotData } from './pivot-utils.js';

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business ファネル詳細ページの dimensions タブ。選択した dimension の値 × ステップ別ユニークユーザー数のピボットテーブルを描画する。dimension 切替で fetch を再発火する。
 * @graph-connects funnelsApi [calls] dimensions API
 * @graph-connects buildPivotData [calls] ピボット変換
 * @graph-connects formatDimensionName [calls] dimension 名ラベル変換
 * @graph-connects formatDimensionValue [calls] dimension 値ラベル変換
 * @graph-connects formatNumber [calls] 数値フォーマット
 */
export function DimensionsTab({
  funnelId,
  funnelDef,
  startDate,
  endDate,
}: {
  funnelId: string;
  funnelDef: { dimensions: string[]; steps?: { name: string }[] } | undefined;
  startDate: string;
  endDate: string;
}) {
  const dimensions = funnelDef?.dimensions ?? [];
  const [selectedDim, setSelectedDim] = useState(dimensions[0] ?? 'device_category');

  const dimQuery = useQuery({
    queryKey: queryKeys.funnels.dimensions(funnelId, startDate, endDate, selectedDim),
    queryFn: () => funnelsApi.getDimensions(funnelId, startDate, endDate, selectedDim),
    staleTime: 5 * 60 * 1000,
    enabled: dimensions.length > 0,
  });

  if (dimensions.length === 0) {
    return (
      <div className="card">
        <p className="text-sm text-[var(--color-text-muted)] text-center py-8">
          ディメンション定義がありません
        </p>
      </div>
    );
  }

  // ステップ × ディメンション値のピボットテーブルを構築（定義順）
  const stepOrder = funnelDef?.steps?.map((s) => s.name) ?? [];
  const pivotData = buildPivotData(dimQuery.data?.data ?? [], stepOrder);

  return (
    <div className="space-y-4">
      {/* ディメンション選択 */}
      <div className="flex gap-2 flex-wrap">
        {dimensions.map((dim) => (
          <button
            key={dim}
            onClick={() => {
              setSelectedDim(dim);
            }}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors border ${
              selectedDim === dim
                ? 'border-[var(--color-accent-primary)] bg-[var(--color-accent-surface)] text-[var(--color-accent-primary)]'
                : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-accent-primary)]'
            }`}
          >
            {formatDimensionName(dim)}
          </button>
        ))}
      </div>

      {/* ピボットテーブル */}
      <div className="card animate-fade-in-up">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">
          {formatDimensionName(selectedDim)} 別内訳
        </h3>

        {dimQuery.isLoading ? (
          <div className="skeleton-shimmer h-48 w-full rounded-lg" />
        ) : pivotData.steps.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)] text-center py-8">
            データがありません
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                    {formatDimensionName(selectedDim)}
                  </th>
                  {pivotData.steps.map((step) => (
                    <th
                      key={step}
                      className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]"
                    >
                      {step}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pivotData.values.map((row) => (
                  <tr
                    key={row.dimensionValue}
                    className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
                  >
                    <td className="py-2 px-3 font-medium text-[var(--color-text-primary)]">
                      {formatDimensionValue(selectedDim, row.dimensionValue)}
                    </td>
                    {pivotData.steps.map((step) => (
                      <td
                        key={step}
                        className="py-2 px-3 text-right font-mono text-[var(--color-text-primary)]"
                      >
                        {formatNumber(row.stepUsers[step] ?? 0)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
